// src/components/FullscreenImageViewer.jsx
//
// Полноэкранный просмотрщик фотографий со свайпами и pinch-to-zoom.
// Свой код, без внешних зависимостей.
//
// Возможности:
// - свайп влево/вправо — следующее/предыдущее фото
// - pinch-to-zoom двумя пальцами
// - двойной тап — переключение зума 1× ↔ 2.5×
// - в режиме зума палец перемещает фото (panning)
// - крестик в углу + свайп вниз для закрытия
// - индикатор «3 из 5» сверху
//
// Props:
//   photos      — array of URLs
//   startIndex  — initial photo index (default 0)
//   onClose     — callback закрытия

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

export default function FullscreenImageViewer({ photos, startIndex = 0, onClose }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const imageRef = useRef(null);
  const lastTapRef = useRef(0);
  const touchStateRef = useRef(null);

  // При смене фото сбрасываем зум
  useEffect(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, [index]);

  // Esc — закрыть
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const goPrev = useCallback(() => {
    setIndex(i => (i > 0 ? i - 1 : i));
  }, []);

  const goNext = useCallback(() => {
    setIndex(i => (i < photos.length - 1 ? i + 1 : i));
  }, [photos.length]);

  // ─── Touch handlers ─────────────────────────────────────────
  const handleTouchStart = (e) => {
    const touches = e.touches;
    const now = Date.now();

    if (touches.length === 1) {
      // Одиночное касание — может быть double tap или drag
      if (now - lastTapRef.current < 300) {
        // Double tap — переключаем зум
        e.preventDefault();
        if (scale > 1) {
          setScale(1);
          setPos({ x: 0, y: 0 });
        } else {
          setScale(2.5);
        }
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;

      touchStateRef.current = {
        type: 'single',
        startX: touches[0].clientX,
        startY: touches[0].clientY,
        startPos: { ...pos },
      };
      setIsDragging(true);
    } else if (touches.length === 2) {
      // Pinch start
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      const initialDist = Math.sqrt(dx * dx + dy * dy);
      touchStateRef.current = {
        type: 'pinch',
        initialDist,
        initialScale: scale,
      };
    }
  };

  const handleTouchMove = (e) => {
    const touches = e.touches;
    const state = touchStateRef.current;
    if (!state) return;

    if (state.type === 'single' && touches.length === 1) {
      const dx = touches[0].clientX - state.startX;
      const dy = touches[0].clientY - state.startY;

      if (scale > 1) {
        // В режиме зума — двигаем фото
        e.preventDefault();
        setPos({ x: state.startPos.x + dx, y: state.startPos.y + dy });
      } else {
        // В обычном режиме — горизонтальный свайп для смены фото
        // (вертикальный — для возможного закрытия свайпом вниз)
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
          e.preventDefault();
        }
        setPos({ x: dx, y: dy });
      }
    } else if (state.type === 'pinch' && touches.length === 2) {
      e.preventDefault();
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const newScale = Math.max(1, Math.min(5, state.initialScale * (dist / state.initialDist)));
      setScale(newScale);
    }
  };

  const handleTouchEnd = (e) => {
    const state = touchStateRef.current;
    if (!state) return;

    if (state.type === 'single' && scale === 1) {
      const dx = pos.x;
      const dy = pos.y;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      // Свайп вниз для закрытия
      if (dy > 80 && absY > absX) {
        onClose();
        return;
      }

      // Горизонтальный свайп для смены фото
      if (absX > 80 && absX > absY) {
        if (dx > 0) goPrev();
        else goNext();
      }

      // Сброс позиции
      setPos({ x: 0, y: 0 });
    }

    // Если зум вернулся к 1 — центрируем
    if (scale <= 1.01) {
      setScale(1);
      setPos({ x: 0, y: 0 });
    }

    touchStateRef.current = null;
    setIsDragging(false);
  };

  if (!photos || photos.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        userSelect: 'none', touchAction: 'none',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Топ-бар: счётчик + закрыть */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'calc(env(safe-area-inset-top) + 12px) 16px 12px',
        color: '#fff',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {photos.length > 1 && t('products.photoOf', { current: index + 1, total: photos.length })}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none',
            width: 36, height: 36, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', cursor: 'pointer',
          }}
          aria-label="Close"
        >
          <Icon name="x" size={18} stroke={2.5} />
        </button>
      </div>

      {/* Фотография */}
      <img
        ref={imageRef}
        src={photos[index]}
        alt=""
        draggable={false}
        style={{
          maxWidth: '100%', maxHeight: '100%',
          objectFit: 'contain',
          transform: `translate3d(${pos.x}px, ${pos.y}px, 0) scale(${scale})`,
          transition: isDragging ? 'none' : 'transform 200ms ease-out',
          transformOrigin: 'center center',
          willChange: 'transform',
        }}
      />

      {/* Стрелки вперёд/назад (для desktop) */}
      {photos.length > 1 && (
        <>
          {index > 0 && (
            <button
              onClick={goPrev}
              style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', cursor: 'pointer', zIndex: 2,
              }}
            >
              <Icon name="chevronLeft" size={24} stroke={2.5} />
            </button>
          )}
          {index < photos.length - 1 && (
            <button
              onClick={goNext}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', cursor: 'pointer', zIndex: 2,
              }}
            >
              <Icon name="chevronRight" size={24} stroke={2.5} />
            </button>
          )}
        </>
      )}

      {/* Точки-индикаторы внизу */}
      {photos.length > 1 && (
        <div style={{
          position: 'absolute', bottom: 'calc(env(safe-area-inset-bottom) + 16px)',
          left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6, zIndex: 2,
        }}>
          {photos.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === index ? 20 : 6, height: 6, borderRadius: 6,
                background: i === index ? '#fff' : 'rgba(255,255,255,0.5)',
                transition: 'all 200ms',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
